/* HybridResearchPanel — Panneau de recherche hybride réutilisable.
 *
 * Affiche un input + le toggle Interne/Web/Hybride + le résultat structuré
 * (synthèse NOAM + sources web cliquables). Injecté dans Trend, Brief
 * comité, Annuaire (fiche entité), Asymétries.
 *
 * Props :
 *   contextModule : "trend" | "annuaire" | "brief" | "generic"
 *   contextId     : id éventuel de l'entité concernée
 *   initialTopic  : pré-remplit le champ recherche (optionnel)
 *   compact       : variante sans bordure (intégrée à un parent)
 *   defaultMode   : "internal" | "web" | "hybrid"
 *
 * Mode strictement additif — n'écrase aucun comportement existant.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Search, Loader2, ExternalLink, Sparkles, AlertTriangle, Globe,
} from "lucide-react";
import AIModeToggle from "@/components/AIModeToggle";

export default function HybridResearchPanel({
  contextModule = "generic",
  contextId = null,
  initialTopic = "",
  defaultMode = "hybrid",
  compact = false,
  testid = "hybrid-research",
  title = "Recherche hybride",
}) {
  const [topic, setTopic] = useState(initialTopic);
  const [mode, setMode] = useState(defaultMode);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!topic.trim()) return;
    // Vague 18.3 — Ne pas reset le résultat précédent : si la requête échoue,
    // on garde l'ancien affiché plutôt que de retourner sur un écran vide.
    setBusy(true); setErr("");
    try {
      const r = await api.post("/workspace/hybrid/research", {
        topic: topic.trim(),
        mode,
        context_module: contextModule,
        context_id: contextId,
        language: "fr",
      });
      setResult(r.data);
    } catch (e) {
      setErr(fmtError(e, "Recherche impossible."));
    } finally { setBusy(false); }
  };

  return (
    <div
      className={compact ? "space-y-3" : "noam-card p-4 space-y-3"}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Globe className="w-3.5 h-3.5 text-[#378ADD]" />
          <div className="noam-overline">{title}</div>
        </div>
        <AIModeToggle value={mode} onChange={setMode} />
      </div>

      <form onSubmit={onSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-2.5" />
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Sujet, entité, réglementation, secteur…"
            className="noam-input text-xs pl-8 w-full"
            disabled={busy}
            data-testid={`${testid}-input`}
          />
        </div>
        <button
          type="submit"
          disabled={busy || !topic.trim()}
          className="btn-primary text-xs disabled:opacity-50"
          data-testid={`${testid}-submit`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {busy ? "Recherche…" : "Lancer"}
        </button>
      </form>

      {err && (
        <div className="flex items-start gap-2 p-2 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-[11px]">
          <AlertTriangle className="w-3 h-3 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      {result && (
        <div className="space-y-3 pt-2 border-t border-white/5" data-testid={`${testid}-result`}>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-zinc-500">
              Mode utilisé : <span className="text-zinc-300 uppercase tracking-wider">{result.mode_used}</span>
            </span>
            {result.web_signals_count > 0 && (
              <span className="text-zinc-500">
                {result.web_signals_count} signaux web · {result.web_sources?.length || 0} sources
              </span>
            )}
          </div>
          <div className="prose prose-invert prose-sm max-w-none text-zinc-200 leading-relaxed whitespace-pre-wrap text-[12px]">
            {result.synthesis}
          </div>
          {result.web_sources?.length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <div className="noam-overline mb-1.5">Sources web</div>
              <ul className="space-y-1">
                {result.web_sources.slice(0, 6).map((s, i) => (
                  <li key={i} className="text-[11px]">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#378ADD] hover:underline inline-flex items-center gap-1"
                    >
                      <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="truncate">{s.title || s.url}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
